import React, { useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { type InsightBoardReading, parseLegacyInsightQuestion } from '~/pages/LeLens/cards/insightBoardEvidence';

import { DUR_STATE, EASE_STATE, MONO_FONT_STACK } from '../chat/uiTokens';

import ImageThumbnails from './ImageThumbnails';
import type { AskUIMessage } from './askUiMessage';
import { userImageParts } from './imageParts';
import { BUBBLE_CORNER, BUBBLE_RADIUS, USER_BUBBLE_MAX_WIDTH, paneTokens } from './paneTokens';
import { userMessageText } from './projectTurn';

/**
 * What a refresh turn attached, when the stored text does not carry it. The pane knows a
 * session was started by a card refresh from its summary (humanInitiated false) and knows the
 * board's cards from the Lens, so it can still say what travelled with the question.
 */
export type UserTurnEvidence = {
  label: string;
  readings?: ReadonlyArray<InsightBoardReading>;
};

function readingSummary(reading: InsightBoardReading): string {
  const rows = reading.rows?.length;
  const parts = [reading.type, reading.status, rows !== undefined ? `${rows} row${rows === 1 ? '' : 's'}` : undefined];
  return parts.filter(Boolean).join(' · ');
}

function EvidenceChip({ evidence }: { evidence: UserTurnEvidence }) {
  const theme = useTheme();
  const tokens = paneTokens(theme);
  const [open, setOpen] = useState(false);
  const expandable = (evidence.readings?.length ?? 0) > 0;
  return (
    <Stack alignItems="flex-end" spacing={0.5} sx={{ maxWidth: USER_BUBBLE_MAX_WIDTH }}>
      <ButtonBase
        aria-expanded={expandable ? open : undefined}
        data-testid="ui-stream-user-evidence"
        disabled={!expandable}
        onClick={() => setOpen((current) => !current)}
        sx={{
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: '8px',
          border: `1px solid ${tokens.hairline}`,
          bgcolor: tokens.assistantBubble,
          fontFamily: MONO_FONT_STACK,
          fontSize: 11,
          color: 'text.secondary',
          '&.Mui-disabled': { color: 'text.secondary' },
        }}
      >
        {evidence.label}
        {expandable ? (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 14,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: `transform ${DUR_STATE}ms ${EASE_STATE}`,
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        ) : null}
      </ButtonBase>
      {expandable ? (
        <Collapse in={open} unmountOnExit sx={{ alignSelf: 'stretch' }}>
          <Stack
            component="ul"
            spacing={0.25}
            sx={{ listStyle: 'none', m: 0, p: 1, border: `1px solid ${tokens.hairline}`, borderRadius: '8px' }}
          >
            {evidence.readings?.map((reading) => (
              <Box component="li" key={reading.title} sx={{ display: 'flex', gap: 1, fontSize: 12.5 }}>
                <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 500, minWidth: 0 }}>
                  {reading.title}
                </Typography>
                <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {readingSummary(reading)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Collapse>
      ) : null}
    </Stack>
  );
}

interface UserTurnProps {
  message: AskUIMessage;
  /** Evidence the pane knows about from the session, for turns stored after the context-channel fix. */
  evidence?: UserTurnEvidence;
  variant?: 'page' | 'pane';
}

/**
 * Your turn stays on the right, with a neutral bubble in the main chat and Sandy Beige in
 * the Lens pane. A legacy insight-refresh message (instruction, question and board readings in one
 * text) is split here, so only the question reads as yours and the readings fold into a chip.
 */
export default function UserTurn({ message, evidence, variant = 'page' }: UserTurnProps) {
  const theme = useTheme();
  const tokens = paneTokens(theme);
  const rawText = userMessageText(message);
  const legacy = useMemo(() => parseLegacyInsightQuestion(rawText), [rawText]);
  const text = legacy?.question ?? rawText;
  const shownEvidence: UserTurnEvidence | undefined = legacy
    ? {
        label:
          legacy.readings.length > 0
            ? `${legacy.readings.length} board reading${legacy.readings.length === 1 ? '' : 's'} attached`
            : 'Board readings attached',
        readings: legacy.readings,
      }
    : evidence;

  return (
    <Stack alignItems="flex-end" spacing={0.75} data-testid="ui-stream-user-turn" sx={{ minWidth: 0 }}>
      <ImageThumbnails images={userImageParts(message)} expanded={variant === 'pane'} />
      <Box
        sx={{
          maxWidth: USER_BUBBLE_MAX_WIDTH,
          px: 1.75,
          py: variant === 'page' ? 1 : 1.25,
          bgcolor: variant === 'page' ? alpha(theme.palette.text.primary, 0.08) : tokens.userBubble,
          borderRadius:
            variant === 'page' ? 1 : `${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_CORNER}px ${BUBBLE_RADIUS}px`,
        }}
      >
        <Typography sx={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {text}
        </Typography>
      </Box>
      {shownEvidence ? <EvidenceChip evidence={shownEvidence} /> : null}
    </Stack>
  );
}
